import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import TrialCountdown from '../components/TrialCountdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://customer-assets.emergentagent.com/job_115f2a70-e012-4ed0-b268-01edb5a7f1fc/artifacts/orzhkwtc_Gemini_Generated_Image_l6tsjbl6tsjbl6ts.png";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, courseRes, quoteRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/courses`),
          axios.get(`${API}/quotes`)
        ]);
        setProducts(prodRes.data.slice(0, 4));
        setCourses(courseRes.data.slice(0, 4));
        setQuotes(quoteRes.data.quotes);
      } catch (e) {
        console.error('Failed to load homepage data', e);
      }
    };
    load();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-[#FFF8F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 md:space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFCA28]/20 rounded-full">
                <Sparkles size={16} className="text-[#FF7043]" />
                <span className="text-sm font-bold tracking-wide uppercase text-[#FF7043]">
                  Lezione di prova gratuita
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-tight">
                Scopri la danza,<br />
                <span className="text-[#FF7043]">balla</span><br />
                <span className="text-[#0288D1]">Blue Moon</span>
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-lg">
                A.S.D. Caribbean Blue Moon Dance School: Baby Dance, Reggaeton, Hip Hop, Caraibico, Tango Argentino e molto altro. Scopri la danza che fa per te!
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/corsi"
                  data-testid="hero-prenota-btn"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#FF7043] text-white font-heading font-bold text-base rounded-full btn-caribbean"
                >
                  Prenota Lezione Gratuita <ArrowRight size={18} />
                </Link>
                <Link
                  to="/shop"
                  data-testid="hero-shop-btn"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0288D1] text-white font-heading font-bold text-base rounded-full btn-caribbean btn-blue"
                >
                  Scopri lo Shop
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-[#FFCA28]/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#FF7043]/15 rounded-full blur-3xl" />
              <img
                src={HERO_IMG}
                alt="Caribbean Blue Moon Dance School"
                className="relative w-full rounded-3xl shadow-2xl shadow-slate-300/40 object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trial Lessons Countdown */}
      <TrialCountdown />

      {/* Motivational Quotes Marquee */}
      {quotes.length > 0 && (
        <section data-testid="quotes-marquee" className="py-8 bg-white border-y border-slate-100">
          <Marquee speed={40} gradient={false} pauseOnHover>
            {quotes.map((q, i) => (
              <span key={i} className="font-accent text-3xl md:text-4xl marquee-gradient mx-12 font-semibold">
                {q}
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* Featured Courses */}
      <section data-testid="courses-preview" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF7043] mb-2">Le nostre discipline</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Trova il tuo ritmo
              </h2>
            </div>
            <Link
              to="/corsi"
              data-testid="see-all-courses"
              className="hidden md:inline-flex items-center gap-1 text-[#0288D1] font-heading font-semibold hover:text-[#FF7043] transition-colors"
            >
              Tutti i corsi <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map(course => (
              <Link
                to="/corsi"
                key={course.id}
                data-testid={`course-card-${course.id}`}
                className="group card-hover rounded-3xl overflow-hidden border border-slate-100 bg-white shadow-lg shadow-slate-100/50"
              >
                <div className="product-image-container aspect-[4/3]">
                  <img src={course.image_url} alt={course.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  {course.age_group && (
                    <span className="inline-block px-3 py-1 bg-[#FFCA28]/20 text-[#FF7043] text-xs font-bold rounded-full mb-2">
                      {course.age_group}
                    </span>
                  )}
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-[#FF7043] transition-colors">
                    {course.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{course.description}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link
              to="/corsi"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue"
            >
              Tutti i Corsi <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section data-testid="products-preview" className="py-20 md:py-28 bg-[#FFF8F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0288D1] mb-2">Shop Ufficiale</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Indossa il tuo stile
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="see-all-products"
              className="hidden md:inline-flex items-center gap-1 text-[#0288D1] font-heading font-semibold hover:text-[#FF7043] transition-colors"
            >
              Vedi tutto <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map(product => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="group"
              >
                <div className="product-image-container rounded-2xl overflow-hidden bg-white aspect-square mb-3 shadow-md shadow-slate-200/40">
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-base text-slate-900 truncate">
                  {product.name.replace(' Caribbean Blue Moon', '')}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <p className="font-heading font-bold text-[#FF7043]">€{product.price.toFixed(2)}</p>
                  <button
                    data-testid={`add-to-cart-${product.id}`}
                    onClick={() => addItem(product, 1, product.sizes ? product.sizes[2] || product.sizes[0] : null)}
                    className="text-xs font-bold text-[#0288D1] hover:text-[#FF7043] transition-colors"
                  >
                    + Aggiungi
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
            >
              Vai allo Shop <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#0288D1] to-[#01579B] text-white">
        <div className="max-w-3xl mx-auto text-center px-6">
          <p className="font-accent text-2xl md:text-3xl text-[#FFCA28] mb-4">La prima lezione è gratuita!</p>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tight mb-6">
            Inizia il tuo viaggio nella danza
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
            9 discipline, dai 4 anni agli adulti. Trova il corso perfetto per te e prenota la tua lezione di prova gratuita.
          </p>
          <Link
            to="/corsi"
            data-testid="cta-prenota-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF7043] text-white font-heading font-bold text-lg rounded-full btn-caribbean animate-pulse-glow"
          >
            Prenota Ora <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
